import { MapPin, ChevronRight, ShoppingBag, Minus, Plus, Trash2, TicketPercent, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { brl } from "@/lib/api";
import { shipInfo } from "@/lib/shop";
import { couponOff } from "@/components/delivery/CouponField";

function ShipRow({ st, ship, onCalc }) {
  const info = shipInfo(st, ship);
  const label = ship.type === "retirada" ? `Retirada no local · pronto em ~${info.eta} min`
    : info.ready ? `Entrega ${info.fee ? brl(info.fee) : "grátis"} · ~${info.eta} min${info.area ? ` · ${info.area}` : ""}` : "Calcular taxa e tempo de entrega";
  return (
    <button onClick={onCalc} className="flex w-full items-center gap-3 border-b border-slate-100 px-5 py-4 text-left transition-colors hover:bg-slate-50" data-testid="shop-calc-btn">
      <MapPin className="h-5 w-5 text-slate-500" /><span className="flex-1 text-sm font-semibold text-slate-800" data-testid="shop-ship-label">{label}</span><ChevronRight className="h-5 w-5 text-slate-500" />
    </button>
  );
}

function CartItem({ i, setQty }) {
  return (
    <li className="flex items-start gap-3 py-3" data-testid={`shop-cart-item-${i.product_id}`}>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800">{i.name}</p>
        {i.notes && <p className="text-xs text-slate-500">{i.notes}</p>}
        <p className="mt-1 text-sm font-semibold">{brl(i.price * i.qty)}</p>
      </div>
      <div className="flex h-9 items-center gap-2 rounded-full border border-slate-200 px-2">
        <button onClick={() => setQty(i.key, -1)} aria-label="Diminuir" data-testid={`shop-cart-dec-${i.product_id}`}>{i.qty === 1 ? <Trash2 className="h-4 w-4 text-red-500" /> : <Minus className="h-4 w-4" />}</button>
        <span className="w-5 text-center text-sm font-semibold">{i.qty}</span>
        <button onClick={() => setQty(i.key, 1)} aria-label="Aumentar" data-testid={`shop-cart-inc-${i.product_id}`}><Plus className="h-4 w-4" /></button>
      </div>
    </li>
  );
}

export function CartPanel({ st, cart, setQty, ship, coupon, onCalc, onCoupon, onRemoveCoupon, onCheckout, color }) {
  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const info = shipInfo(st, ship);
  const fee = info.ready ? info.fee : 0;
  const off = couponOff(coupon, ship.type, subtotal, fee);
  const total = subtotal + fee - off;
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="shop-cart-panel">
      <ShipRow st={st} ship={ship} onCalc={onCalc} />
      {cart.length === 0 ? (
        <div className="grid place-items-center gap-2 py-14 text-slate-400" data-testid="shop-cart-empty"><ShoppingBag className="h-20 w-20" strokeWidth={1.2} /><p className="text-xl">Sacola vazia</p></div>
      ) : (
        <ul className="max-h-[38vh] divide-y divide-slate-100 overflow-y-auto px-5">{cart.map((i) => <CartItem key={i.key} i={i} setQty={setQty} />)}</ul>
      )}
      {coupon ? (
        <div className="flex items-center gap-3 border-t border-slate-100 px-5 py-4 text-sm" data-testid="shop-coupon-applied">
          <TicketPercent className="h-6 w-6 text-emerald-600" /><span className="flex-1"><b className="font-mono">{coupon.code}</b> · {coupon.label}</span>
          <button onClick={onRemoveCoupon} aria-label="Remover cupom" data-testid="shop-coupon-remove"><X className="h-4 w-4 text-slate-500" /></button>
        </div>
      ) : (
        <button onClick={onCoupon} className="flex w-full items-center gap-3 border-t border-slate-100 px-5 py-4 text-left transition-colors hover:bg-slate-50" data-testid="shop-coupon-btn">
          <TicketPercent className="h-7 w-7 text-slate-400" /><span className="flex-1"><span className="block text-sm font-semibold text-slate-800">Tem um cupom?</span><span className="text-sm text-slate-500">Clique e insira o código</span></span><ChevronRight className="h-5 w-5 text-slate-500" />
        </button>
      )}
      {cart.length > 0 && (
        <div className="space-y-1 border-t border-slate-100 px-5 py-3 text-sm text-slate-600">
          <div className="flex justify-between"><span>Subtotal</span><span>{brl(subtotal)}</span></div>
          {ship.type === "delivery" && <div className="flex justify-between"><span>Entrega</span><span>{info.ready ? brl(fee) : "a calcular"}</span></div>}
          {off > 0 && <div className="flex justify-between text-emerald-700"><span>Cupom</span><span>- {brl(off)}</span></div>}
          <div className="flex justify-between pt-1 text-base font-bold text-slate-900"><span>Total</span><span data-testid="shop-cart-total">{brl(total)}</span></div>
        </div>
      )}
      <div className="p-4">
        <Button className="h-12 w-full text-base font-semibold text-white" style={{ background: color }} disabled={!cart.length} onClick={onCheckout} data-testid="shop-checkout-btn">
          {cart.length ? `Finalizar pedido · ${brl(total)}` : "Sacola vazia"}
        </Button>
      </div>
    </div>
  );
}
